'use client';

import { createContext, useContext, ReactNode } from 'react';
import { PageProps } from 'libs/contentful/queries/page/props';
import { ResolvedVisualSettings } from 'utils/pageHelpers/visualSettings';

interface PageContextType {
  page?: PageProps;
  visualSettings?: ResolvedVisualSettings;
}

const PageContext = createContext<PageContextType>({});

/**
 * Context provider for page-level data.
 * Provides a centralized way to access page-level data within page components.
 *
 * @example
 * <PageProvider page={page} visualSettings={visualSettings}>
 *   <Component />
 * </PageProvider>
 */
export const PageProvider = ({
  children,
  page,
  visualSettings,
}: {
  children: ReactNode;
  page?: PageProps;
  visualSettings?: ResolvedVisualSettings;
}) => {
  return <PageContext.Provider value={{ page, visualSettings }}>{children}</PageContext.Provider>;
};

/**
 * Hook to access page-level data.
 *
 * @example
 * const Component = () => {
 *   const { page } = usePage();
 *   return <div>{page?.title}</div>;
 * }
 */
export const usePage = () => {
  const context = useContext(PageContext);
  return context;
};
